import type { GetStaticPaths, GetStaticProps } from 'next';
import type { ParsedUrlQuery } from 'querystring';
import React from 'react';

import CustomerLogos from '@components/CustomerLogos';
import CustomersSection from '@components/CustomersSection';
import CustomHead from '@components/CustomHead';
import FiveCardGridSection from '@components/FiveCardGridSection';
import Footer from '@components/FooterNew';
import Header from '@components/HeaderNew';
import HeroBottom from '@components/HeroBottom';
import HeroSection from '@components/product/HeroSection';
import MainBenefitsSection from '@components/product/MainBenefitsSection';
import SeeWhatsPossibleSection from '@components/product/SeeWhatsPossibleSection';
import SubfeaturesSection from '@components/product/SubfeaturesSection';
import SupportedConnectionsSection from '@components/product/SupportedConnectionsSection';
import { ProductPageContext } from '@contexts/PageContext';
import { revalidate } from '@utils/constants';
import { getProductPage, getProductPages } from '@utils/contentfulClient';
import { parseContentfulData } from '@utils/contentfulFunctions';
import { ContentfulImage } from '@utils/propTypes';

interface Params extends ParsedUrlQuery {
  product: string;
}

export const getStaticPaths: GetStaticPaths<Params> = async () => {
  const records = await getProductPages();
  return {
    fallback: false,
    paths: records.map((record) => ({
      params: {
        product: (record.fields.slug as string).split('/')[2],
      },
    })),
  };
};

export const getStaticProps: GetStaticProps<
  { data: Awaited<ReturnType<typeof getProductPage>>['fields'] },
  Params
> = async (ctx) => {
  const { product } = ctx.params;
  try {
    return {
      props: {
        data: (await getProductPage(`/product/${product}`))?.fields,
      },
      revalidate,
    };
  } catch (error) {
    console.error(error);
    return {
      props: {
        data: null,
        err: 'Something went wrong 😕',
      },
    };
  }
};

type parsedDataType = {
  ledeText?: string;
  logosSection?: {
    heading: string;
    logos: ContentfulImage[];
  };
  metaTags?: {
    description: string;
    image: ContentfulImage;
    title: string;
  };
};

const Product = ({ data }) => {
  const parsedData: parsedDataType = parseContentfulData(data);
  const { metaTags, ledeText, logosSection } = parsedData;

  const card3 =
    ledeText === 'Connections'
      ? null
      : {
          title: 'Enterprise grade security',
          text: 'SOC 2 and GDPR compliance, SSO/SCIM integrations, versatile database connectivity, and precise access controls.',
          image: `${process.env.NEXT_PUBLIC_ASSET_HOST}/static/images/enterprise_security.png`,
        };

  return (
    <>
      <CustomHead
        meta={{
          title: metaTags.title,
          description: metaTags.description,
          image: metaTags.image.file.url,
        }}
        openGraph={{
          title: metaTags.title,
          description: metaTags.description,
          image: metaTags.image.file.url,
        }}
      />
      <main>
        <ProductPageContext.Provider value={parsedData}>
          <Header />
          <HeroSection />

          <CustomerLogos
            spacing="pb-6 lg:pb-20 pt-10 lg:pt-20"
            heading={logosSection.heading}
            logos={logosSection.logos}
          />
          <MainBenefitsSection />
          <SubfeaturesSection />
          {ledeText === 'Charts' && <SeeWhatsPossibleSection />}
          {ledeText === 'Connections' && <SupportedConnectionsSection />}
          <FiveCardGridSection title="Why PopSQL?" card_3={card3} />
          <CustomersSection
            col1_ids={[5, 6]}
            col2_ids={[2, 7]}
            col3_ids={[3, 9]}
          />
          <HeroBottom
            icon={`${process.env.NEXT_PUBLIC_ASSET_HOST}/static/homepage/popsql_app_icon.png`}
            title="The most powerful workspace for data teams"
            description=""
            spacing="mt-20 md:mt-52 mb-20 md:mb-40 px-4 gap-4"
          />
          {/* <FaqSection
            heading={data.faq_section_heading}
            listOfFaqs={listOfFaqs}
          /> */}
        </ProductPageContext.Provider>
      </main>
      <Footer />
    </>
  );
};

export default Product;
